/**
 * Telefone de contato.
 *
 * Guardamos os telefones **só com dígitos**, sem máscara e sem o código do país
 * (`11912345678`). Assim o mesmo dado serve para mostrar na tela, para o link do
 * WhatsApp e para montar a lista de um grupo — sem depender de como a pessoa
 * digitou.
 */
/** Quantidade de dígitos de um telefone brasileiro: DDD + número. */
export const DIGITOS_TELEFONE = { minimo: 10, maximo: 11 };
export const DDD_VALIDOS = [
    11, 12, 13, 14, 15, 16, 17, 18, 19, 21, 22, 24, 27, 28, 31, 32, 33, 34, 35, 37, 38, 41, 42, 43, 44, 45, 46,
    47, 48, 49, 51, 53, 54, 55, 61, 62, 63, 64, 65, 66, 67, 68, 69, 71, 73, 74, 75, 77, 79, 81, 82, 83, 84,
    85, 86, 87, 88, 89, 91, 92, 93, 94, 95, 96, 97, 98, 99,
];
/** Deixa só os dígitos, tirando o código do país quando vier (+55 ...). */
export function somenteDigitos(valor) {
    const digitos = (valor ?? '').replace(/\D/g, '');
    if (digitos.length > DIGITOS_TELEFONE.maximo && digitos.startsWith('55')) {
        return digitos.slice(2);
    }
    return digitos;
}
/** O telefone está completo e com um DDD que existe? */
export function telefoneValido(valor) {
    const digitos = somenteDigitos(valor);
    if (digitos.length < DIGITOS_TELEFONE.minimo || digitos.length > DIGITOS_TELEFONE.maximo)
        return false;
    if (/^(\d)\1+$/.test(digitos))
        return false;
    const ddd = Number(digitos.slice(0, 2));
    if (!DDD_VALIDOS.includes(ddd))
        return false;
    // Celular tem 9 dígitos e começa com 9; fixo tem 8 e começa de 2 a 5.
    const numero = digitos.slice(2);
    if (numero.length === 9)
        return numero.startsWith('9');
    return /^[2-5]/.test(numero);
}
/**
 * Telefone pronto para gravar: `11912345678`, ou `null` quando está incompleto.
 * É o formato que o link do WhatsApp e a lista do grupo esperam.
 */
export function normalizarTelefone(valor) {
    if (!valor)
        return null;
    const digitos = somenteDigitos(valor);
    return telefoneValido(digitos) ? digitos : null;
}
/** Máscara de leitura: `(11) 91234-5678` ou `(11) 3456-7890`. */
export function formatarTelefone(valor) {
    const digitos = somenteDigitos(valor ?? '');
    if (digitos.length === 0)
        return '';
    if (digitos.length <= 2)
        return `(${digitos}`;
    if (digitos.length <= 6)
        return `(${digitos.slice(0, 2)}) ${digitos.slice(2)}`;
    if (digitos.length <= 10) {
        return `(${digitos.slice(0, 2)}) ${digitos.slice(2, 6)}-${digitos.slice(6)}`;
    }
    return `(${digitos.slice(0, 2)}) ${digitos.slice(2, 7)}-${digitos.slice(7, 11)}`;
}
/** Número com o código do país, como o WhatsApp usa: `5511912345678`. */
export function telefoneComPais(valor) {
    const digitos = normalizarTelefone(valor);
    return digitos ? `55${digitos}` : null;
}
/** Link para abrir (ou criar) a conversa no WhatsApp. */
export function linkWhatsApp(valor, mensagem) {
    const numero = telefoneComPais(valor);
    if (!numero)
        return null;
    const texto = mensagem?.trim();
    return `https://wa.me/${numero}${texto ? `?text=${encodeURIComponent(texto)}` : ''}`;
}
/**
 * Lista de telefones prontos para colar num grupo.
 *
 * O WhatsApp aceita vários números separados por vírgula ao criar o grupo —
 * é assim que o administrador junta os clientes de uma vez.
 */
export function listaParaGrupo(valores) {
    return valores
        .map((valor) => telefoneComPais(valor))
        .filter((valor) => Boolean(valor))
        .join(', ');
}
//# sourceMappingURL=telefone.js.map